import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getImageUrl } from '../api';
import { KM_VARS, KM_BASE } from './km-styles';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getCustomerId = () => {
    const customer = JSON.parse(localStorage.getItem('customer'));
    return customer?.id || null;
};

const Checkout = () => {
    const navigate = useNavigate();

    // ── Auth State ───────────────────────────────────────
    const [isLoggedIn,   setIsLoggedIn]   = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);

    useEffect(() => {
        setIsLoggedIn(!!localStorage.getItem('customer'));
        setCheckingAuth(false);
    }, []);

    // ── Cart State ───────────────────────────────────────
    const [cartItems,   setCartItems]   = useState([]);
    const [cartLoading, setCartLoading] = useState(true);

    useEffect(() => {
        const customerId = getCustomerId();
        if (!customerId) { setCartLoading(false); return; }
        fetch(`${API_BASE_URL}/cart/customer/${customerId}`)
            .then(res => res.json())
            .then(data => setCartItems(Array.isArray(data) ? data : []))
            .catch(() => {
                const local = JSON.parse(localStorage.getItem('cart')) || [];
                setCartItems(local.map(item => ({ ...item, quantity: item.quantity || 1 })));
            })
            .finally(() => setCartLoading(false));
    }, [isLoggedIn]);

    // ── Delivery Zones ───────────────────────────────────
    const [deliveryZones,    setDeliveryZones]    = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [deliveryCharge,   setDeliveryCharge]   = useState(0);

    useEffect(() => {
        fetch(`${API_BASE_URL}/delivery-zones/active`)
            .then(res => res.json())
            .then(data => setDeliveryZones(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    const handleCityChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, city: value }));

        const typed = value.trim().toLowerCase();
        if (!typed) {
            setDeliveryCharge(0);
            setSelectedDistrict('');
            return;
        }

        const matchedZone = deliveryZones.find(
            zone => zone.district.toLowerCase() === typed
        );

        if (matchedZone) {
            setDeliveryCharge(parseFloat(matchedZone.charge));
            setSelectedDistrict(matchedZone.district);
        } else {
            setDeliveryCharge(0);
            setSelectedDistrict('');
        }
    };

    // ── Billing Form State ───────────────────────────────
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', businessName: '',
        address: '', apartment: '', city: '', postCode: '',
        phone: '', email: '', orderNotes: '',
    });

    // ── Shipping Address State ───────────────────────────
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [shippingData,  setShippingData]  = useState({
        firstName: '', lastName: '', businessName: '',
        address: '', apartment: '', city: '', postCode: '',
    });

    const handleSameAsBilling = (e) => {
        setSameAsBilling(e.target.checked);
        if (e.target.checked) {
            setShippingData({
                firstName:    formData.firstName,
                lastName:     formData.lastName,
                businessName: formData.businessName,
                address:      formData.address,
                apartment:    formData.apartment,
                city:         formData.city,
                postCode:     formData.postCode,
            });
        } else {
            setShippingData({ firstName: '', lastName: '', businessName: '', address: '', apartment: '', city: '', postCode: '' });
        }
    };

    // ── Coupon State ─────────────────────────────────────
    const [couponInput,     setCouponInput]     = useState('');
    const [couponStatus,    setCouponStatus]    = useState(null);
    const [couponMsg,       setCouponMsg]       = useState('');
    const [appliedCoupon,   setAppliedCoupon]   = useState(null);
    const [appliedDiscount, setAppliedDiscount] = useState(0);
    const [isSubmitting,    setIsSubmitting]    = useState(false);

    const [showCouponModal,  setShowCouponModal]  = useState(false);
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [activeFilter,     setActiveFilter]     = useState('all');
    const [modalManualInput, setModalManualInput] = useState('');
    const [modalError,       setModalError]       = useState('');

    useEffect(() => {
        fetch(`${API_BASE_URL}/coupons/available`)
            .then(res => res.json())
            .then(data => setAvailableCoupons(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    // ── Price Calculations ───────────────────────────────
    const subtotal = cartItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0) * (item.quantity || 1), 0);
    // Tax removed — total = subtotal + delivery - discount only
    const total    = subtotal + deliveryCharge - appliedDiscount;

    // ── Input Handlers ───────────────────────────────────
    const handleInputChange    = (e) => setFormData(prev    => ({ ...prev, [e.target.name]: e.target.value }));
    const handleShippingChange = (e) => setShippingData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    // ── Coupon Logic ─────────────────────────────────────
    const applyCode = async (code, { setErr } = {}) => {
        const trimmed = code.trim().toUpperCase();
        const showError = setErr || ((msg) => { setCouponStatus('error'); setCouponMsg(msg); });

        if (!trimmed) { showError('Please enter a coupon code.'); return false; }

        try {
            const res  = await fetch(`${API_BASE_URL}/coupons/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: trimmed, subtotal }),
            });
            const data = await res.json();

            if (!data.success) {
                showError(data.message || 'Invalid coupon.');
                if (!setErr) { setAppliedDiscount(0); setAppliedCoupon(null); }
                return false;
            }

            setAppliedDiscount(data.discount);
            setAppliedCoupon({ code: data.code, label: data.label, type: data.type, value: data.value });
            setCouponStatus('success');
            setCouponMsg(`Coupon "${data.code}" applied — ${data.label}!`);
            setCouponInput('');
            return true;
        } catch {
            showError('Could not validate coupon. Try again.');
            return false;
        }
    };

    const handleQuickApply   = async (code) => {
        const ok = await applyCode(code, { setErr: setModalError });
        if (ok) setTimeout(() => setShowCouponModal(false), 500);
    };

    const handleModalManual  = async () => {
        setModalError('');
        const ok = await applyCode(modalManualInput, { setErr: setModalError });
        if (ok) { setModalManualInput(''); setTimeout(() => setShowCouponModal(false), 500); }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setAppliedDiscount(0);
        setCouponInput('');
        setCouponStatus(null);
        setCouponMsg('');
    };

    const filteredCoupons = activeFilter === 'all'
        ? availableCoupons
        : availableCoupons.filter(c => c.type === activeFilter);

    // ── Order Submit ─────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (cartItems.length === 0) {
            Swal.fire({ icon: 'error', title: 'Cart Empty', text: 'Your cart is empty!', confirmButtonColor: '#185FA5' });
            return;
        }

        if (!formData.firstName || !formData.lastName || !formData.address || !formData.city || !formData.postCode || !formData.phone || !formData.email) {
            Swal.fire({ icon: 'warning', title: 'Missing Details', text: 'Please fill all required billing fields.', confirmButtonColor: '#185FA5' });
            return;
        }

        setIsSubmitting(true);
        try {
            const orderRes  = await fetch(`${API_BASE_URL}/razorpay/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount: total }),
            });
            const orderData = await orderRes.json();

            if (!orderData.success) {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Could not initiate payment. Try again.', confirmButtonColor: '#185FA5' });
                setIsSubmitting(false);
                return;
            }

            const options = {
                key:         orderData.key || process.env.REACT_APP_RAZORPAY_KEY,
                amount:      orderData.amount,
                currency:    orderData.currency,
                name:        'Kalpanamart',
                description: 'Order Payment',
                order_id:    orderData.orderId,
                prefill: {
                    name:    `${formData.firstName} ${formData.lastName}`,
                    email:   formData.email,
                    contact: formData.phone,
                },
                theme: { color: '#1A52A8' },

                handler: async function (response) {
                    const verifyRes  = await fetch(`${API_BASE_URL}/razorpay/verify-payment`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            razorpay_order_id:   response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature:  response.razorpay_signature,
                        }),
                    });
                    const verifyData = await verifyRes.json();

                    if (verifyData.success) {
                        navigate('/thank-you', {
                            state: {
                                paymentId:      response.razorpay_payment_id,
                                orderId:        response.razorpay_order_id,
                                amount:         total,
                                name:           `${formData.firstName} ${formData.lastName}`,
                                email:          formData.email,
                                items:          cartItems,
                                coupon:         appliedCoupon,
                                discount:       appliedDiscount,
                                deliveryCharge,
                                district:       selectedDistrict || formData.city,
                            },
                        });

                        const saveAndClean = async () => {
                            await fetch(`${API_BASE_URL}/orders/save`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    customer_id:      getCustomerId(),
                                    customer_name:    `${formData.firstName} ${formData.lastName}`,
                                    customer_email:   formData.email,
                                    customer_phone:   formData.phone,
                                    billing_address:  JSON.stringify({ ...formData }),
                                    shipping_address: sameAsBilling
                                        ? JSON.stringify({ ...formData })
                                        : JSON.stringify(shippingData),
                                    order_notes:      formData.orderNotes,
                                    subtotal,
                                    tax:              0,        // tax removed
                                    delivery_charge:  deliveryCharge,
                                    district:         selectedDistrict || formData.city,
                                    discount:         appliedDiscount,
                                    total,
                                    coupon_code:      appliedCoupon?.code || null,
                                    payment_id:       response.razorpay_payment_id,
                                    razorpay_order_id: response.razorpay_order_id,
                                    items: cartItems.map(item => ({
                                     product_id: item.productId || item.product_id || item.id,
                                        variant_id: item.variant_id || item.variantId || item.Variant?.id || null,
                                        name:       item.name,
                                        price:      parseFloat(item.price) || 0,
                                        quantity:   item.quantity || 1,
                                        image:      item.img,
                                        weight:     item.weight || null,
                                    })),
                                }),
                            });

                            // ✅ Email is sent automatically inside /orders/save on the backend

                            const cid = getCustomerId();
                            if (cid) await fetch(`${API_BASE_URL}/cart/clear/${cid}`, { method: 'DELETE' });
                            localStorage.removeItem('cart');
                        };

                        saveAndClean().catch(err => console.error('Post-order tasks failed:', err));

                    } else {
                        Swal.fire({ icon: 'error', title: 'Verification Failed', text: 'Payment could not be verified.', confirmButtonColor: '#185FA5' });
                    }
                },

                modal: {
                    ondismiss: () => {
                        setIsSubmitting(false);
                        Swal.fire({ icon: 'info', title: 'Payment Cancelled', text: 'You closed the payment window.', confirmButtonColor: '#185FA5' });
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', (r) => {
                setIsSubmitting(false);
                Swal.fire({ icon: 'error', title: 'Payment Failed', text: r.error.description || 'Something went wrong.', confirmButtonColor: '#185FA5' });
            });
            rzp.open();
            setIsSubmitting(false);

        } catch (err) {
            console.error(err);
            setIsSubmitting(false);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong. Please try again.', confirmButtonColor: '#185FA5' });
        }
    };

    // ── Auth Guards ──────────────────────────────────────
    if (checkingAuth) return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE}</style>
            <div className="km-full">
                <div className="km-loader">
                    <div className="km-spinner" />
                    <p>Checking session…</p>
                </div>
            </div>
        </section>
    );

    if (!isLoggedIn) return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE}</style>
            <div className="km-full">
                <div className="km-empty">
                    <div className="km-empty-icon">🔒</div>
                    <h4>Login Required</h4>
                    <p>Please log in to proceed with checkout.</p>
                    <Link to="/account" className="km-btn-primary">Login / Register</Link>
                </div>
            </div>
        </section>
    );

    // ── Main Render ──────────────────────────────────────
    return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE + CO_STYLES}</style>

            {/* ── Coupon Modal ── */}
            {showCouponModal && (
                <div className="co-overlay" onClick={() => setShowCouponModal(false)}>
                    <div className="co-modal" onClick={e => e.stopPropagation()}>
                        <div className="co-modal-hd">
                            <span className="co-modal-title">🏷️ Available Coupon Offers</span>
                            <button className="co-modal-close" onClick={() => setShowCouponModal(false)}>×</button>
                        </div>
                        <div className="co-modal-filters">
                            {[
                                { key: 'all',     label: 'All Offers'  },
                                { key: 'percent', label: '% Cashback'  },
                                { key: 'fixed',   label: 'Flat Off'    },
                            ].map(f => (
                                <button
                                    key={f.key}
                                    className={`co-filter-btn${activeFilter === f.key ? ' active' : ''}`}
                                    onClick={() => setActiveFilter(f.key)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <div className="co-modal-input-row">
                            <input
                                type="text"
                                className="co-modal-input"
                                placeholder="Enter coupon code manually"
                                value={modalManualInput}
                                onChange={e => { setModalManualInput(e.target.value); setModalError(''); }}
                                onKeyDown={e => e.key === 'Enter' && handleModalManual()}
                            />
                            <button className="co-modal-apply-btn" onClick={handleModalManual}>Apply</button>
                        </div>
                        {modalError && <div className="co-modal-error">⚠ {modalError}</div>}
                        <div className="co-modal-list">
                            {filteredCoupons.length === 0 ? (
                                <div className="co-modal-empty">No offers available right now.</div>
                            ) : filteredCoupons.map(c => {
                                const isApplied = appliedCoupon?.code === c.code;
                                return (
                                    <div key={c.code} className="co-coupon-card">
                                        <div className="co-cc-icon" style={{ background: c.type === 'percent' ? '#7b1fa2' : '#1A52A8' }}>
                                            {c.code?.[0] || 'K'}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="co-cc-label">
                                                {c.type === 'percent' ? `Get ${c.value}% Cashback` : `Flat ₹${c.value} Off`}
                                            </div>
                                            <div className="co-cc-desc">
                                                {c.description || (c.min_order > 0 ? `Min order ₹${c.min_order}` : 'No minimum order required')}
                                            </div>
                                            <span className="co-cc-code">{c.code}</span>
                                            {c.expires_at && (
                                                <div className="co-cc-exp">
                                                    Expires: {new Date(c.expires_at).toLocaleDateString('en-IN')}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className={`co-cc-apply${isApplied ? ' applied' : ''}`}
                                            onClick={() => !isApplied && handleQuickApply(c.code)}
                                        >
                                            {isApplied ? '✓ Applied' : 'Apply'}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div className="km-full">

                <div className="km-ph">
                    <h1>Checkout</h1>
                    <p>Secure &amp; fast payment</p>
                </div>

                <form onSubmit={handleSubmit} className="co-layout">

                    {/* ── Left Column: Billing Form ── */}
                    <div className="co-form-col">
                        <div className="km-card" style={{ overflow: 'visible' }}>
                            <div className="km-card-hd">
                                <span className="km-ibadge"><span className="km-bdot" />Billing Details</span>
                            </div>
                            <div style={{ padding: '24px 28px' }}>

                                <div className="co-grid-2">
                                    <div className="co-field">
                                        <label className="co-lbl">First Name <span className="co-req">*</span></label>
                                        <input type="text" name="firstName" required className="co-input" placeholder="First Name" onChange={handleInputChange} />
                                    </div>
                                    <div className="co-field">
                                        <label className="co-lbl">Last Name <span className="co-req">*</span></label>
                                        <input type="text" name="lastName" required className="co-input" placeholder="Last Name" onChange={handleInputChange} />
                                    </div>
                                </div>

                                <div className="co-field">
                                    <label className="co-lbl">Business Name</label>
                                    <input type="text" name="businessName" className="co-input" placeholder="Business Name (Optional)" onChange={handleInputChange} />
                                </div>

                                <div className="co-field">
                                    <label className="co-lbl">Street Address <span className="co-req">*</span></label>
                                    <input type="text" name="address" required className="co-input" placeholder="House number and street name" onChange={handleInputChange} />
                                </div>

                                <div className="co-field">
                                    <input type="text" name="apartment" className="co-input" placeholder="Apartment, suite, unit, etc. (Optional)" onChange={handleInputChange} />
                                </div>

                         {/* District Dropdown */}
<div className="co-field">
    <label className="co-lbl">
        District <span className="co-req">*</span>
    </label>

    <select
        name="city"
        required
        className="co-input"
        value={formData.city}
        onChange={handleCityChange}
    >
        <option value="">Select District</option>

        {deliveryZones.map((zone) => (
            <option key={zone.id} value={zone.district}>
                {zone.district}
            </option>
        ))}
    </select>
</div>

                                <div className="co-field">
                                    <label className="co-lbl">Postcode / ZIP <span className="co-req">*</span></label>
                                    <input type="text" name="postCode" required className="co-input" placeholder="Postcode / ZIP" onChange={handleInputChange} />
                                </div>

                                <div className="co-grid-2">
                                    <div className="co-field">
                                        <label className="co-lbl">Phone <span className="co-req">*</span></label>
                                        <input type="tel" name="phone" required className="co-input" placeholder="Phone" onChange={handleInputChange} />
                                    </div>
                                    <div className="co-field">
                                        <label className="co-lbl">Email Address <span className="co-req">*</span></label>
                                        <input type="email" name="email" required className="co-input" placeholder="Email Address" onChange={handleInputChange} />
                                    </div>
                                </div>

                                <div className="co-ship-toggle">
                                    <label className="co-check-label">
                                        <input
                                            type="checkbox"
                                            className="co-check-inp"
                                            checked={sameAsBilling}
                                            onChange={handleSameAsBilling}
                                        />
                                        <span className="co-checkmark" />
                                        Same as Billing Address
                                    </label>
                                </div>

                                {!sameAsBilling && (
                                    <div className="co-ship-section">
                                        <div className="co-section-ttl" style={{ marginBottom: 16 }}>
                                            <span className="co-ttl-bar" />Shipping Address
                                        </div>
                                        <div className="co-grid-2">
                                            <div className="co-field">
                                                <label className="co-lbl">First Name <span className="co-req">*</span></label>
                                                <input type="text" name="firstName" required className="co-input" placeholder="First Name" value={shippingData.firstName} onChange={handleShippingChange} />
                                            </div>
                                            <div className="co-field">
                                                <label className="co-lbl">Last Name <span className="co-req">*</span></label>
                                                <input type="text" name="lastName" required className="co-input" placeholder="Last Name" value={shippingData.lastName} onChange={handleShippingChange} />
                                            </div>
                                        </div>
                                        <div className="co-field">
                                            <label className="co-lbl">Business Name</label>
                                            <input type="text" name="businessName" className="co-input" placeholder="Business Name (Optional)" value={shippingData.businessName} onChange={handleShippingChange} />
                                        </div>
                                        <div className="co-field">
                                            <label className="co-lbl">Street Address <span className="co-req">*</span></label>
                                            <input type="text" name="address" required className="co-input" placeholder="House number and street name" value={shippingData.address} onChange={handleShippingChange} />
                                        </div>
                                        <div className="co-field">
                                            <input type="text" name="apartment" className="co-input" placeholder="Apartment, suite, unit, etc. (Optional)" value={shippingData.apartment} onChange={handleShippingChange} />
                                        </div>
                                        <div className="co-grid-2">
                                            <div className="co-field">
                                                <label className="co-lbl">Town / City <span className="co-req">*</span></label>
                                                <input type="text" name="city" required className="co-input" placeholder="Town / City" value={shippingData.city} onChange={handleShippingChange} />
                                            </div>
                                            <div className="co-field">
                                                <label className="co-lbl">Postcode / ZIP <span className="co-req">*</span></label>
                                                <input type="text" name="postCode" required className="co-input" placeholder="Postcode / ZIP" value={shippingData.postCode} onChange={handleShippingChange} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="co-section-ttl"><span className="co-ttl-bar" />Additional Information(optional)</div>
                                <textarea
                                    name="orderNotes"
                                    className="co-textarea"
                                    rows="4"
                                    placeholder="Notes about your order, e.g. special notes for delivery."
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Right Column: Order Summary ── */}
                    <div className="co-summary-col">

                        <div className="km-sum-card" style={{ marginBottom: 16, position: 'static' }}>
                            <div className="km-sum-hd">
                                <div className="km-sum-title">Your Order</div>
                                <div className="km-swoosh" />
                            </div>

                            <div style={{ padding: '4px 0' }}>
                                <div className="co-order-hdr">
                                    <span>Product</span><span>Subtotal</span>
                                </div>
                                {cartLoading ? (
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <div className="km-spinner" style={{ margin: '0 auto 8px', width: 28, height: 28 }} />
                                    </div>
                                ) : cartItems.length > 0 ? cartItems.map(item => (
                                    <div className="co-prod-row" key={item.id}>
                                        {item.img && (
                                            <div className="co-prod-img-w">
                                                <img src={getImageUrl(item.img)} alt={item.name} onError={e => e.target.style.display = 'none'} />
                                            </div>
                                        )}
                                        <div className="co-prod-info">
                                            <div className="co-prod-name">{item.name}</div>
                                            {(item.weight || item.variantName) && (
                                                <div className="co-prod-meta">
                                                    {item.weight || `${item.variantName || ''}${item.unit ? ' ' + item.unit : ''}`}
                                                </div>
                                            )}
                                            <div className="co-prod-qty">× {item.quantity}</div>
                                        </div>
                                        <span className="co-prod-price">
                                            ₹{((parseFloat(item.price) || 0) * item.quantity).toFixed(2)}
                                        </span>
                                    </div>
                                )) : (
                                    <div style={{ fontSize: 13, color: '#555', padding: '12px 22px', textAlign: 'center' }}>
                                        No items in cart
                                    </div>
                                )}
                            </div>

                            {/* ✅ Price Breakdown — Estimated Tax row நீக்கப்பட்டது */}
                            <div style={{ padding: '0 22px 20px' }}>
                                <div className="km-sdiv" />

                                <div className="km-srow">
                                    <span>Subtotal</span>
                                    <span className="km-sval">₹{subtotal.toFixed(2)}</span>
                                </div>

                                {/* Delivery row — match ஆனபோது மட்டும் காட்டும் */}
                                {selectedDistrict && (
                                    <div className="km-srow">
                                        <span>Delivery</span>
                                        <span className={`km-sval ${deliveryCharge === 0 ? 'co-free-val' : ''}`}>
                                            {deliveryCharge === 0 ? '🎉 Free' : `₹${deliveryCharge.toFixed(2)}`}
                                        </span>
                                    </div>
                                )}

                                {appliedCoupon && (
                                    <div className="km-srow">
                                        <span className="km-disc-lbl">Coupon ({appliedCoupon.code})</span>
                                        <span className="km-disc-val">−₹{appliedDiscount.toFixed(2)}</span>
                                    </div>
                                )}

                                <div className="km-sdiv" />

                                <div className="km-total-row">
                                    <span className="km-total-lbl">Total</span>
                                    <span className="km-total-val">₹{total.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Coupon Section */}
                        <div style={{ marginBottom: 12 }}>
                            {appliedCoupon ? (
                                <div className="co-applied-coupon">
                                    <span>🏷️ {appliedCoupon.code} — {appliedCoupon.label}</span>
                                    <button type="button" onClick={handleRemoveCoupon} className="co-remove-coupon">✕</button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => { setShowCouponModal(true); setModalError(''); }}
                                    className="co-coupon-trigger"
                                >
                                    🏷️ Apply Coupon / View Offers
                                </button>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting || cartLoading}
                            className="km-checkout-btn"
                            style={{ fontSize: 14, padding: '15px 24px' }}
                        >
                            {isSubmitting ? 'Processing…' : 'Place Order →'}
                        </button>

                        <div className="km-trust" style={{ marginTop: 10 }}>
                            <span><i className="ph ph-lock" /> Secure Checkout</span>
                            <span><i className="ph ph-shield-check" /> Safe Payment</span>
                            <span><i className="ph ph-truck" /> Fast Delivery</span>
                        </div>
                    </div>

                </form>
            </div>
        </section>
    );
};

// ── Styles (unchanged) ───────────────────────────────────────────────────────
const CO_STYLES = `
    .co-layout {
        display: grid;
        grid-template-columns: 1fr 360px;
        gap: 24px;
        align-items: start;
    }
    @media(max-width:1024px){ .co-layout { grid-template-columns: 1fr; } }

    .co-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
    @media(max-width:600px){ .co-grid-2 { grid-template-columns:1fr; } }

    .co-field { margin-bottom: 14px; }
    .co-lbl   { font-size:12px; font-weight:600; color:var(--km-blue-deep); display:block; margin-bottom:5px; }
    .co-req   { color:#E53935; }

    .co-input {
        width:100%; padding:11px 16px; border:1px solid var(--km-bdr);
        border-radius:10px; font-size:13px; color:var(--km-txt); background:#fff;
        outline:none; transition:border-color .2s, box-shadow .2s; box-sizing:border-box;
        font-family:inherit;
    }
    .co-input::placeholder { color:#aaa; }
    .co-input:focus { border-color:var(--km-blue-mid); box-shadow:0 0 0 3px rgba(26,82,168,.1); }

    .co-delivery-badge {
        display:inline-flex; align-items:center; gap:6px;
        padding:6px 14px; border-radius:20px; font-size:12px; font-weight:700;
        background:#EEF3FA; color:var(--km-blue-mid); border:1px solid var(--km-bdr);
        animation:co-fade .2s ease;
    }
    .co-delivery-badge.free    { background:#E8F5E9; color:#2E7D32; border-color:#A5D6A7; }
    .co-delivery-badge.warning { background:#FFF8E1; color:#F57F17; border-color:#FFE082; }
    .co-free-val { color:#2E7D32 !important; font-weight:700; }

    .co-textarea {
        width:100%; padding:11px 16px; border:1px solid var(--km-bdr);
        border-radius:10px; font-size:13px; color:var(--km-txt); background:#fff;
        outline:none; resize:vertical; transition:border-color .2s, box-shadow .2s;
        font-family:inherit; box-sizing:border-box;
    }
    .co-textarea:focus { border-color:var(--km-blue-mid); box-shadow:0 0 0 3px rgba(26,82,168,.1); }

    .co-section-ttl {
        display:flex; align-items:center; gap:10px;
        font-size:14px; font-weight:700; color:var(--km-blue-deep);
        margin: 22px 0 14px; padding-bottom:12px; border-bottom:1.5px solid var(--km-blue-mid);
    }
    .co-ttl-bar { width:4px; height:18px; background:var(--km-orange); border-radius:4px; display:inline-block; }

    .co-ship-toggle { margin:20px 0; padding:14px 18px; background:var(--km-blue-xl); border:0.5px solid var(--km-bdr); border-radius:10px; }
    .co-check-label { display:flex; align-items:center; gap:10px; cursor:pointer; font-size:13px; font-weight:600; color:var(--km-blue-deep); user-select:none; }
    .co-check-inp   { display:none; }
    .co-checkmark   { width:18px; height:18px; border:2px solid var(--km-blue-mid); border-radius:4px; flex-shrink:0; background:#fff; display:flex; align-items:center; justify-content:center; transition:background .15s; position:relative; }
    .co-check-inp:checked + .co-checkmark { background:var(--km-blue-mid); border-color:var(--km-blue-mid); }
    .co-check-inp:checked + .co-checkmark::after { content:'✓'; color:#fff; font-size:11px; font-weight:700; position:absolute; }

    .co-ship-section { margin-top:18px; padding:20px; background:#F7FBFF; border:0.5px dashed #85B7EB; border-radius:12px; animation:co-fade .25s ease; }
    @keyframes co-fade { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }

    .co-order-hdr  { display:flex; justify-content:space-between; padding:12px 22px 8px; font-size:10px; font-weight:700; color:var(--km-blue-mid); text-transform:uppercase; letter-spacing:.08em; border-bottom:1px solid var(--km-blue-xl); }
    .co-prod-row   { display:flex; align-items:flex-start; gap:10px; padding:10px 22px; border-bottom:1px solid var(--km-blue-xl); }
    .co-prod-row:last-child { border-bottom:none; }
    .co-prod-img-w { width:40px; height:40px; border-radius:8px; background:var(--km-blue-xl); border:1px solid var(--km-bdr); overflow:hidden; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .co-prod-img-w img { width:100%; height:100%; object-fit:contain; padding:3px; }
    .co-prod-info  { flex:1; min-width:0; }
    .co-prod-name  { font-size:12px; font-weight:600; color:var(--km-txt); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .co-prod-meta  { font-size:10px; color:var(--km-txt3); margin-top:2px; }
    .co-prod-qty   { font-size:11px; color:var(--km-blue-mid); font-weight:600; margin-top:2px; }
    .co-prod-price { font-size:13px; color:var(--km-orange); font-weight:700; white-space:nowrap; }

    .co-applied-coupon { background:var(--km-green-l); border-radius:12px; padding:10px 16px; display:flex; align-items:center; justify-content:space-between; border:1px solid #A5D6A7; }
    .co-applied-coupon span { font-size:13px; color:var(--km-green); font-weight:600; }
    .co-remove-coupon { background:none; border:0.5px solid var(--km-red); color:var(--km-red); border-radius:20px; padding:3px 10px; font-size:12px; cursor:pointer; transition:all .15s; }
    .co-remove-coupon:hover { background:var(--km-red); color:#fff; }

    .co-coupon-trigger {
        width:100%; border:1.5px dashed var(--km-bdr); background:var(--km-blue-xl);
        border-radius:10px; padding:11px 0; font-size:13px; color:var(--km-blue-mid);
        font-weight:700; cursor:pointer; font-family:inherit; transition:all .18s;
    }
    .co-coupon-trigger:hover { border-color:var(--km-blue-mid); background:#e8f0ff; }

    .co-overlay    { position:fixed; inset:0; background:rgba(0,0,0,0.5); z-index:9999; display:flex; align-items:center; justify-content:center; padding:16px; }
    .co-modal      { background:#fff; border-radius:14px; width:100%; max-width:460px; max-height:88vh; display:flex; flex-direction:column; box-shadow:0 12px 48px rgba(0,0,0,.22); overflow:hidden; animation:co-modal-in .22s ease; }
    @keyframes co-modal-in { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    .co-modal-hd   { display:flex; justify-content:space-between; align-items:center; padding:16px 20px; border-bottom:1px solid var(--km-blue-xl); flex-shrink:0; background:linear-gradient(90deg,var(--km-blue-deep),var(--km-blue-mid)); }
    .co-modal-title { font-size:14px; font-weight:700; color:#fff; }
    .co-modal-close { background:none; border:none; font-size:22px; cursor:pointer; color:rgba(255,255,255,.7); line-height:1; }
    .co-modal-close:hover { color:#fff; }
    .co-modal-filters    { display:flex; gap:8px; padding:12px 20px; border-bottom:1px solid var(--km-blue-xl); flex-shrink:0; flex-wrap:wrap; }
    .co-filter-btn       { padding:5px 14px; border-radius:20px; border:1.5px solid var(--km-bdr); background:#fff; color:#555; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; }
    .co-filter-btn:hover, .co-filter-btn.active { background:var(--km-blue-mid); color:#fff; border-color:var(--km-blue-mid); }
    .co-modal-input-row  { display:flex; gap:8px; padding:12px 20px; border-bottom:1px solid var(--km-blue-xl); flex-shrink:0; }
    .co-modal-input      { flex:1; padding:9px 14px; border:1.5px solid var(--km-bdr); border-radius:8px; font-size:13px; outline:none; color:var(--km-txt); transition:border-color .2s; font-family:inherit; }
    .co-modal-input:focus { border-color:var(--km-blue-mid); }
    .co-modal-apply-btn  { padding:9px 18px; background:var(--km-orange); color:#fff; border:none; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap; transition:background .15s; font-family:inherit; }
    .co-modal-apply-btn:hover { background:#e56500; }
    .co-modal-error { font-size:12px; color:#E53935; padding:4px 20px 8px; flex-shrink:0; }
    .co-modal-list  { overflow-y:auto; padding:12px 20px; display:flex; flex-direction:column; gap:12px; }
    .co-modal-empty { text-align:center; padding:32px 0; font-size:13px; color:#aaa; }

    .co-coupon-card { border:1.5px dashed var(--km-bdr); border-radius:10px; padding:14px 16px; display:flex; align-items:flex-start; gap:12px; transition:border-color .2s,box-shadow .2s; }
    .co-coupon-card:hover { border-color:var(--km-blue-mid); box-shadow:0 2px 12px rgba(26,82,168,.08); }
    .co-cc-icon  { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; color:#fff; flex-shrink:0; }
    .co-cc-label { font-size:14px; font-weight:700; color:var(--km-blue-deep); }
    .co-cc-desc  { font-size:12px; color:#888; margin-top:3px; line-height:1.5; }
    .co-cc-code  { display:inline-block; background:#F7FBFF; border:1px dashed var(--km-bdr); border-radius:4px; padding:3px 10px; font-size:11px; font-weight:700; letter-spacing:1.5px; color:var(--km-blue-mid); margin-top:8px; }
    .co-cc-exp   { font-size:11px; color:var(--km-orange); margin-top:4px; }
    .co-cc-apply { padding:6px 16px; background:#fff; color:var(--km-blue-mid); border:1.5px solid var(--km-blue-mid); border-radius:20px; font-size:12px; font-weight:700; cursor:pointer; white-space:nowrap; transition:all .15s; flex-shrink:0; font-family:inherit; }
    .co-cc-apply:hover   { background:var(--km-blue-mid); color:#fff; }
    .co-cc-apply.applied { background:var(--km-green); color:#fff; border-color:var(--km-green); }

    select.co-input {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3e%3cpath fill='%23666' d='M5.5 7.5l4.5 4.5 4.5-4.5'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 16px;
    padding-right: 40px;
    cursor: pointer;
}
`;

export default Checkout;